import React from "react";
import EligibilityForm from "./LoanCalculator";
import { Check, ShieldCheck } from "lucide-react";
import { PreQualifierTile } from "./PreQualifier";
import { usePreQualifier } from "@/context/PreQualifierContext";

export default function Hero() {
  const preq = usePreQualifier();
  return (
    <section className="relative hero-grad pt-4 md:pt-6 pb-20 md:pb-28">
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start pt-8 md:pt-12">
          {/* LEFT */}
          <div className="lg:col-span-7 rise-in">
            <div className="inline-flex items-center gap-2 pl-1 pr-4 py-1 rounded-full bg-emerald-soft mb-7" data-testid="hero-trust-pill">
              <span className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-emerald text-white text-[10.5px] font-extrabold uppercase tracking-[0.08em]">
                <ShieldCheck className="h-3 w-3" strokeWidth={2.6} />
                Trusted
              </span>
              <span className="text-[12.5px] font-bold text-emerald-3">By 8,200+ UK Businesses</span>
            </div>

            <h1 className="font-display font-extrabold text-[44px] sm:text-[58px] lg:text-[72px] leading-[1.02] tracking-[-0.035em] text-navy">
              Business finance
              <br />
              <span className="text-emerald">without the runaround.</span>
              <br />
              <span className="text-navy/40">£5k to £500M.</span>
            </h1>

            <p className="mt-6 text-[17px] md:text-[18px] leading-[1.6] text-navy/70 max-w-xl font-medium">
              From <b className="text-navy">working capital to £500M growth deals</b>, we match your business with the right lender from our funding panel. Indicative terms in hours — not weeks.
            </p>

            <ul className="mt-8 space-y-3.5">
              <TickItem><b className="text-navy">Indicative offer in 60 seconds.</b> Bespoke terms same day.</TickItem>
              <TickItem><b className="text-navy">Funds drawn down in as little as 24 hours</b> on qualifying facilities.</TickItem>
              <TickItem><b className="text-navy">Soft eligibility check only</b> — no impact on the business credit file.</TickItem>
              <TickItem><b className="text-navy">17 funding purposes covered</b> — from VAT bills to acquisitions.</TickItem>
            </ul>

            <div className="mt-12 grid grid-cols-4 gap-6 max-w-2xl pt-8 border-t border-navy/10">
              <Stat value="£2.4B+" label="Funded since 2019" />
              <Stat value="8,200+" label="UK businesses funded" />
              <Stat value="24hrs" label="Avg. terms turnaround" />
              <Stat value="96%" label="Client satisfaction" />
            </div>

            <PreQualifierTile onOpen={() => preq?.open()} />
          </div>

          {/* RIGHT — Eligibility card */}
          <div className="lg:col-span-5 rise-in" style={{ animationDelay: "120ms" }}>
            <EligibilityForm />
          </div>
        </div>
      </div>
    </section>
  );
}

function TickItem({ children }) {
  return (
    <li className="flex items-start gap-3 text-[15.5px] text-navy/80">
      <span className="h-6 w-6 rounded-full bg-emerald flex items-center justify-center shrink-0 mt-0.5">
        <Check className="h-3.5 w-3.5 text-white" strokeWidth={3.2} />
      </span>
      <span>{children}</span>
    </li>
  );
}
function Stat({ value, label }) {
  return (
    <div>
      <div className="font-display font-extrabold text-[24px] md:text-[32px] leading-none tracking-[-0.04em] text-navy">{value}</div>
      <div className="text-[11px] text-navy/55 mt-2 font-semibold leading-tight">{label}</div>
    </div>
  );
}
